import React, { useState } from 'react';
import { Button, Card, Input, Modal, Paragraph, colors } from '@dexma/ui-components';
import { Menu } from 'antd';
import styled from 'styled-components';
import i18n from 'asset/translation';
import gtm from 'adapter/in/reports/config/gtm';
import { AddWidgetCatalogItem, AddWidgetCategory } from 'adapter/in/reports/components/AddWidgetDropdown/useAddWidgetCatalog';

const GROUP_ITEM_CLASS_NAME = 'add-widget-modal-menu-group-item';

const DEFAULT_CLOSE_BUTTON_ARIA_LABEL = 'add-widget-modal-default-close-button';

type AddWidgetModalProps = {
    open: boolean;
    onClose: () => void;
    categories: AddWidgetCategory[];
    selectedCategoryKey: string;
    onSelectCategory: (key: string) => void;
    searchTerm: string;
    onSearchChange: (event: React.FormEvent<HTMLInputElement>) => void;
    items: AddWidgetCatalogItem[];
    onItemClick: (key: string) => void;
};

export const AddWidgetModal = ({
    open,
    onClose,
    categories,
    selectedCategoryKey,
    onSelectCategory,
    searchTerm,
    onSearchChange,
    items,
    onItemClick,
}: AddWidgetModalProps) => {
    const [expandedCategoryKeys, setExpandedCategoryKeys] = useState<string[]>([]);

    const toggleExpandedCategory = (categoryKey: string) => {
        setExpandedCategoryKeys((current) =>
            current.includes(categoryKey) ? current.filter((key) => key !== categoryKey) : [...current, categoryKey]
        );
    };

    const menuItems = categories.flatMap((category) => {
        const hasGroups = !!category.groups?.length;
        const isExpanded =
            expandedCategoryKeys.includes(category.key) || !!category.groups?.some((group) => group.key === selectedCategoryKey);

        const categoryItem = {
            key: category.key,
            label: (
                <StyledAddWidgetModalCategoryLabel>
                    <span>{`${category.label} (${category.items.length})`}</span>
                    {hasGroups && (
                        <StyledAddWidgetModalExpandButton
                            kind='iconButton'
                            type='button'
                            variant='icon-secondary'
                            iconBefore={isExpanded ? 'chevron_up' : 'chevron_down'}
                            data-testid={`${gtm.addWidgetModal.category}.expand.${category.key}`}
                            onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                                toggleExpandedCategory(category.key);
                            }}
                            iconAriaLabel={i18n.t(
                                isExpanded
                                    ? 'reports.reportTemplate.addWidgetModal.collapseCategoryAriaLabel'
                                    : 'reports.reportTemplate.addWidgetModal.expandCategoryAriaLabel'
                            )}
                        />
                    )}
                </StyledAddWidgetModalCategoryLabel>
            ),
        };

        const groupItems =
            isExpanded && category.groups
                ? category.groups.map((group) => ({
                      key: group.key,
                      label: `${group.label} (${group.items.length})`,
                      className: GROUP_ITEM_CLASS_NAME,
                  }))
                : [];

        return [categoryItem, ...groupItems];
    });

    return (
        <StyledAddWidgetModal
            open={open}
            width={900}
            onCancel={onClose}
            maskClosable
            centered
            destroyOnClose
            title={
                <StyledAddWidgetModalTitleBar>
                    <span>{i18n.t('reports.reportTemplate.addWidgetModal.title')}</span>
                    <Button
                        kind='iconButton'
                        type='button'
                        variant='icon-secondary'
                        iconBefore='close'
                        onClick={onClose}
                        dataId={gtm.addWidgetModal.close}
                        data-testid={gtm.addWidgetModal.close}
                        iconAriaLabel={i18n.t('common.ariaLabel.closeModalIcon')}
                    />
                </StyledAddWidgetModalTitleBar>
            }
            closeModalButtonAriaLabel={DEFAULT_CLOSE_BUTTON_ARIA_LABEL}
            body={
                <StyledAddWidgetModalBody>
                    <StyledAddWidgetModalMenu
                        mode='inline'
                        selectedKeys={[selectedCategoryKey]}
                        onClick={({ key }: { key: string }) => onSelectCategory(String(key))}
                        items={menuItems}
                        data-testid={gtm.addWidgetModal.category}
                    />
                    <StyledAddWidgetModalContent>
                        <StyledAddWidgetModalSearch>
                            <Input
                                dataId={gtm.addWidgetModal.search}
                                type='text'
                                placeholder={i18n.t('reports.reportTemplate.addWidgetModal.searchPlaceholder')}
                                icon='search'
                                onChange={onSearchChange}
                                value={searchTerm}
                                data-testid={gtm.addWidgetModal.search}
                                isItToSearch
                                label={i18n.t('reports.reportTemplate.addWidgetModal.searchPlaceholder')}
                            />
                        </StyledAddWidgetModalSearch>
                        <StyledAddWidgetModalCards>
                            <StyledAddWidgetModalCardsGrid>
                                {items.map((item) => (
                                    <StyledAddWidgetModalCard key={item.key} data-id={item.dataId} data-testid={item.dataId}>
                                        <Card
                                            title={item.label}
                                            description={item.description}
                                            onClick={() => onItemClick(item.key)}
                                            footer={<StyledAddWidgetModalCardTag>{item.tagLabel}</StyledAddWidgetModalCardTag>}
                                        />
                                    </StyledAddWidgetModalCard>
                                ))}
                            </StyledAddWidgetModalCardsGrid>
                            {items.length === 0 && (
                                <Paragraph margin='1rem 0 0 0'>{i18n.t('reports.reportTemplate.addWidgetModal.noResults')}</Paragraph>
                            )}
                        </StyledAddWidgetModalCards>
                    </StyledAddWidgetModalContent>
                </StyledAddWidgetModalBody>
            }
        />
    );
};

const StyledAddWidgetModal = styled(Modal)`
    #modal-title {
        margin-bottom: 0px !important;
        padding: 16px;
        border-bottom: 1px solid ${colors.gray200};
    }

    [aria-label='${DEFAULT_CLOSE_BUTTON_ARIA_LABEL}'] {
        display: none;
    }

    .ant-modal-content {
        padding: 0px;
    }
`;

const StyledAddWidgetModalTitleBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const StyledAddWidgetModalBody = styled.div`
    display: flex;
    align-items: stretch;
    height: min(32rem, 60vh);
    overflow: hidden;
`;

const StyledAddWidgetModalMenu = styled(Menu)`
    width: 30%;
    flex-shrink: 0;
    padding: 16px 0;
    border-right: 1px solid ${colors.gray200};
    overflow-y: auto;

    &.ant-menu {
        text-align: left;
    }

    .ant-menu-item {
        text-align: left;
    }

    .ant-menu-item-selected {
        background-color: ${colors.irisBlue} !important;
        color: ${colors.white};
    }

    .${GROUP_ITEM_CLASS_NAME} {
        padding-left: 56px !important;
    }
`;

const StyledAddWidgetModalCategoryLabel = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const StyledAddWidgetModalExpandButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
`;

const StyledAddWidgetModalContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
`;

const StyledAddWidgetModalSearch = styled.div`
    padding: 16px;
    border-bottom: 1px solid ${colors.gray200};

    .icon-container {
        order: 2;
        border-radius: 0 4px 4px 0;
        flex-shrink: 0;
        padding-right: 1.5rem !important;
    }

    input {
        order: 1;
        padding-left: 0.75rem !important;
        border-radius: 4px 0 0 4px !important;
    }

    .icon-container svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

const StyledAddWidgetModalCards = styled.div`
    flex: 1;
    min-height: 0;
    padding: 16px;
    overflow-y: auto;
`;

const StyledAddWidgetModalCardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: start;
    gap: 16px;
`;

const StyledAddWidgetModalCard = styled.div`
    > div {
        display: flex;
        flex-direction: column;
        border: 1px solid ${colors.gray200};
        box-shadow: none !important;

        &:hover {
            box-shadow: none !important;
        }
    }

    .card-body {
        padding: 16px;
    }
`;

const StyledAddWidgetModalCardTag = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid ${colors.gray200};
    border-radius: 4px;
    background-color: ${colors.gray100};
    color: ${colors.gray600};
    font-size: 0.75rem;
    font-weight: 600;
`;
